// Per-Gate practice content: breath patterns, meditation slugs, creative prompts.
// Kept as a parallel structure so authoring remains close to GATES in gates.ts.

export interface BreathPattern {
  label: string;
  // Inhale / hold / exhale / hold (seconds). 0 = skip that phase.
  inhale: number;
  holdIn: number;
  exhale: number;
  holdOut: number;
  cycles: number;
}

export interface GatePractice {
  /** A short (one-line) creative prompt — no upload required. */
  creative: string;
  /** Suggested breath rhythm to sit with. */
  breath: BreathPattern;
  /** Thematic sibling suggestion (a second gate to consider besides n+1). */
  siblingGate: number;
}

// Reusable breath patterns
const CALM: BreathPattern = { label: "Even four", inhale: 4, holdIn: 0, exhale: 4, holdOut: 0, cycles: 8 };
const BOX:  BreathPattern = { label: "Box breath", inhale: 4, holdIn: 4, exhale: 4, holdOut: 4, cycles: 6 };
const LONG: BreathPattern = { label: "Long exhale", inhale: 4, holdIn: 0, exhale: 8, holdOut: 0, cycles: 6 };
const DEEP: BreathPattern = { label: "Deep sixes", inhale: 6, holdIn: 2, exhale: 6, holdOut: 2, cycles: 6 };

// Sibling map — thematic pairings (curated where obvious, else n+2 fallback).
const SIBLINGS: Record<number, number> = {
  1: 8, 2: 10, 3: 4, 4: 3, 5: 27, 6: 26, 7: 8, 8: 20, 9: 32, 10: 17,
  11: 18, 12: 27, 13: 33, 14: 15, 15: 25, 16: 19, 17: 10, 18: 27,
  19: 20, 20: 23, 21: 32, 22: 28, 23: 20, 24: 30, 25: 31, 26: 27,
  27: 5, 28: 29, 29: 35, 30: 25, 31: 25, 32: 23, 33: 34, 34: 27,
  35: 29, 36: 27, 37: 39, 38: 45, 39: 42, 40: 44, 41: 47, 42: 46,
  43: 44, 44: 40, 45: 47, 46: 42, 47: 1,
};

const CREATIVE: Record<number, string> = {
  1: "Choose one object in your room and study it for ninety seconds without looking away.",
  2: "Write four lines that begin with 'I breathe in… I breathe out…'.",
  3: "Sit in complete silence for five minutes; write the first sentence that surfaces.",
  4: "Draw the shape of your stillness — a single line, no lifting the pen.",
  5: "List three practices you will keep no matter what this month.",
  6: "Write a memory you want to rehearse so it becomes part of you.",
  7: "Compose one sentence you would be willing to carve into stone.",
  8: "Recall a conversation and write only what the other person said.",
  9: "Describe your workshop of the self — real or imagined — in five sentences.",
  10: "Sketch your ideal day as a clock face with words at each hour.",
  11: "Name one thing you will fast from this week and write why.",
  12: "Write a devotion in ten lines to something you love without wavering.",
  13: "Name an ancestor and write the question you think they were asking.",
  14: "List the frequencies you let in today — sound, voice, media — and mark each with + or −.",
  15: "Describe the world you are projecting through your eyes right now.",
  16: "Write about your last intentional touch — what did it transmit?",
  17: "Draft a short 'sleep prayer' in three lines.",
  18: "Write your body a letter thanking it for one specific service.",
  19: "Draw your heart-compass and label where the needle currently points.",
  20: "Write a sentence of compassion for someone you have judged this week.",
  21: "Divide a page: True but not useful / Useful and true. Fill it.",
  22: "Write the sentence of your clearest boundary this month.",
  23: "Write a letter of forgiveness you will not send.",
  24: "List five things you are grateful for that you have stopped noticing.",
  25: "Describe an ordinary thing today as if you had never seen it before.",
  26: "Write one paragraph on what you are studying and what it is teaching you to see.",
  27: "Design the smallest possible version of a practice you keep abandoning.",
  28: "Write the name of one person you will serve tomorrow, and how.",
  29: "Describe the vessel you are building for the wealth you are asking for.",
  30: "Photograph or draw one beautiful thing near you and title it.",
  31: "Write about the most sacred ordinary moment of your day.",
  32: "Write the shadow you are refusing to look at — one paragraph.",
  33: "Write the name the current is calling you by in this season.",
  34: "Write a vow, in daylight, for the version of you who shows up at midnight.",
  35: "List what you are carrying, and mark which is heavier than your capacity.",
  36: "Plot this week's hours as seeds — draw what you hope grows.",
  37: "Write your own eulogy in a single paragraph — then edit toward the truth.",
  38: "Name three fears and, beside each, the practice that shrinks it.",
  39: "Write a short parable about a truth you learned this year.",
  40: "Describe the elder version of yourself in three sentences.",
  41: "List three things you learned by teaching them to someone.",
  42: "Write a letter to your future self ten years out.",
  43: "Describe your relationship with time in one metaphor.",
  44: "Write about a small return home — internal or external.",
  45: "Draw the mandala of your current life — five circles, five words.",
  46: "Write the invocation you say when you begin work.",
  47: "Write the first sentence of the next chapter of your journey.",
};

// Breath pattern per gate — mapped by theme.
const BREATH_MAP: Record<number, BreathPattern> = {
  1: CALM, 2: DEEP, 3: LONG, 4: LONG, 5: BOX, 6: CALM, 7: CALM, 8: LONG, 9: LONG, 10: BOX,
  11: LONG, 12: DEEP, 13: DEEP, 14: CALM, 15: CALM, 16: CALM, 17: LONG, 18: DEEP, 19: DEEP, 20: LONG,
  21: BOX, 22: BOX, 23: LONG, 24: CALM, 25: CALM, 26: BOX, 27: BOX, 28: CALM, 29: BOX, 30: CALM,
  31: LONG, 32: LONG, 33: DEEP, 34: BOX, 35: BOX, 36: BOX, 37: LONG, 38: LONG, 39: DEEP, 40: DEEP,
  41: CALM, 42: DEEP, 43: BOX, 44: LONG, 45: CALM, 46: DEEP, 47: DEEP,
};

export function getGatePractice(n: number): GatePractice {
  return {
    creative: CREATIVE[n] ?? "Sit with this gate. Let one sentence surface, and write it down.",
    breath: BREATH_MAP[n] ?? CALM,
    siblingGate: SIBLINGS[n] ?? Math.min(47, n + 2),
  };
}

// Actions a member can record on a Gate.
export const GATE_ACTIONS = [
  "read_reflection",
  "played_music",
  "finished_meditation",
  "wrote_journal",
  "did_creative",
  "did_breath",
] as const;
export type GateAction = (typeof GATE_ACTIONS)[number];

/** Minimum distinct actions before a Gate is considered "walked through". Soft threshold. */
export const GATE_COMPLETION_THRESHOLD = 3;
